import { memo, useMemo } from "react";

import { Breadcrumbs } from "@components/Breadcrumbs";
import { useCollection } from "@react/hooks/useCollection";
import { getShopifyGlobalId } from "@root/tablescape/utilities";
import { useSectionSettings } from "@sections/Root/SectionSettingsContext";
import { type CollectionSectionSettings } from "@tablescape/state";

type props = {
  breadcrumbsLabel: string;
  homeLabel: string;
};

const CollectionBreadcrumbsBase = ({ breadcrumbsLabel, homeLabel }: props) => {
  const {
    breadcrumbs: { links },
    collectionId,
  } = useSectionSettings<CollectionSectionSettings>();

  const { breadcrumbs, handle, isLoading, title } = useCollection(
    getShopifyGlobalId("Collection", collectionId)
  );

  const breadcrumbLinks = useMemo(() => {
    const collectionUrl = `/collections/${handle}`;

    if (breadcrumbs) {
      return [
        ...breadcrumbs.map((breadcrumb) => ({
          title: breadcrumb.titleOverride?.value ?? breadcrumb.title,
          url: breadcrumb.onlineStoreUrl ?? "",
        })),
        { title: title ?? "", url: collectionUrl },
      ];
    } else if (links) {
      for (const first of links) {
        if (first.url === collectionUrl) return [first];

        for (const second of first.links ?? []) {
          if (second.url === collectionUrl) return [first, second];

          for (const third of second.links ?? []) {
            if (third.url === collectionUrl) return [first, second, third];
          }
        }
      }
    }

    return [];
  }, [links, breadcrumbs, handle, title]);

  return (
    <Breadcrumbs
      breadcrumbsLabel={breadcrumbsLabel}
      homeLabel={homeLabel}
      isLoading={isLoading}
      items={breadcrumbLinks}
    />
  );
};

export const CollectionBreadcrumbs = memo(CollectionBreadcrumbsBase);
